import React from "react";
import {Group, Image, Layer, Rect, Text} from "react-konva";
import {useStage} from "@/components/konva/stage/ResponsiveStageHooks.ts";
import type {MapTransform} from "@/encounter/model/ViewPort.ts";
import type {Visibility} from "@/tabletop/visibility/VisibilityModel.ts";
import {placeFogFrame, uncoveredFogRegions} from "@/tabletop/visibility/SoftFogPlacement.ts";
import type {FogFrame} from "@/tabletop/visibility/useSoftFogWorker.ts";

export default function PlayerMapLayers({children, decorations, overlay, transform, visibility, fogFrame, fogUnavailable}: {
    children: React.ReactNode;
    decorations?: React.ReactNode;
    overlay: React.ReactNode;
    transform: MapTransform;
    visibility: Visibility;
    fogFrame?: FogFrame;
    fogUnavailable: boolean;
}) {
    const {stageSize} = useStage();
    const fogPlacement = fogFrame ? placeFogFrame(fogFrame.scene, transform) : undefined;
    const uncovered = uncoveredFogRegions(stageSize, fogPlacement);
    const clip = visibility.enabled ? (context: import("konva/lib/Context").Context) => {
        context.beginPath();
        visibility.polygons.forEach(polygon => {
            if (polygon.length < 3) return;
            context.moveTo(polygon[0].x, polygon[0].y);
            polygon.slice(1).forEach(point => context.lineTo(point.x, point.y));
            context.closePath();
        });
    } : undefined;

    return <>
        <Layer>
            <Rect width={stageSize.width} height={stageSize.height}
                  fill={visibility.enabled ? "#080c14" : "#222"} listening={false}/>
            <Group x={transform.x} y={transform.y} scaleX={transform.scale} scaleY={transform.scale}>
                <Group clipFunc={clip}>
                    {children}
                </Group>
            </Group>
            <Group x={transform.x} y={transform.y} scaleX={transform.scale} scaleY={transform.scale}
                clipFunc={clip}>{decorations}</Group>
        </Layer>
        {visibility.enabled && !fogUnavailable && <Layer listening={false}>
            {fogFrame && fogPlacement && <Image image={fogFrame.bitmap} {...fogPlacement} listening={false}/>}
            {fogFrame && fogPlacement && <Image image={fogFrame.tintBitmap} {...fogPlacement}
                                                globalCompositeOperation="lighter" listening={false}/>}
            {uncovered.map((region, index) =>
                <Rect key={index} {...region} fill="#080c14" listening={false}/>)}
        </Layer>}
        <Layer listening={false}>
            <Group x={transform.x} y={transform.y} scaleX={transform.scale} scaleY={transform.scale}>{overlay}</Group>
        </Layer>
        {visibility.enabled && fogUnavailable && <Layer listening={false}>
            <Text y={24} width={stageSize.width} align="center" fill="#f5caca"
                  fontSize={16} text="Sichtdarstellung nicht verfügbar" listening={false}/>
        </Layer>}
    </>;
}
